import { API_DOMENS, BASE_API_URL } from '../../constants/apis.constants';
import IContextContainer from '../context/icontext-container';
import { ReducerMethods } from '../store/reducer.methods';
import { BaseService, HttpRequestConfig } from './base.service';

enum HttpMethods {
  GET = 'GET',
  POST = 'POST',
  PATCH = 'PATCH',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export class HttpService extends BaseService {
  constructor(ctx: IContextContainer) {
    super(ctx);
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.patch = this.patch.bind(this);
    this.delete = this.delete.bind(this);
    this.toFormData = this.toFormData.bind(this);
  }

  private getFullApiUrl(path: string, domain: string = API_DOMENS.SB_API) {
    return `${BASE_API_URL}/${domain}/${path}`;
  }

  private getAccessToken() {
    const token = JSON.parse(localStorage.getItem('tokens') || '{}');
    if (!token) return null;
    return `Bearer ${token}`;
  }

  private spreadConfig<ReqBody extends Object>(config: HttpRequestConfig<ReqBody>) {
    const headers: any = config?.headers || {};
    const token = headers['Authorization'] || this.getAccessToken();
    if (!config?.omitAuth && token) headers['Authorization'] = token;
    let url = config.isFullUrl ? config.url : this.getFullApiUrl(config.url, config.domain);
    const accessKey = JSON.parse(localStorage.getItem("user_MessageObj") || '{}');
    const params = {
      ...(config.params ?? {}),
      ...(config.omitAuth ? {} : { accessKey })
    };
    const queryString = new URLSearchParams(params).toString();
    url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
    return {
      ...config,
      url,
      headers,
    } as HttpRequestConfig<ReqBody>;
  }

  protected pageEnum(pageEnum: number) {
    return `/GetPageData?pageEnum=${pageEnum}`;
  }

  protected toFormData<T extends Object>(body: T) {
    const formData = new FormData();
    Object.entries(body).map(value => {
      if (value[1] === undefined) {
        return;
      }
      if (value[1] instanceof FileList) {
        for (let i = 0; i < value[1].length; ++i) {
          formData.append(`${value[0]}`, value[1].item(i) || '');
        }
      } else if (value[1] instanceof Array) {
        formData.append(`${value[0]}`, JSON.stringify(value[1]));
      } else if (value[1] instanceof Object && !(value[1] instanceof File)) {
        Object.keys(value[1]).map(key => {
          if (value[1][key] !== undefined) {
            formData.append(`${value[0]}[${key}]`, value[1][key]);
          }
        });
      } else {
        formData.append(value[0], value[1]);
      }
    });
    return formData;
  }

  protected get = <ResBody extends Object>(
    config: HttpRequestConfig<Object>,
    reducerMethod: ReducerMethods
  ) => {
    return this.requestResult<{}, ResBody>(
      this.spreadConfig(config),
      HttpMethods.GET,
      reducerMethod
    );
  };

  protected post = <ReqBody extends Object, ResBody extends Object>(
    config: HttpRequestConfig<ReqBody>,
    reducerMethod: ReducerMethods
  ) => {
    return this.requestResult<ReqBody, ResBody>(
      this.spreadConfig(config),
      HttpMethods.POST,
      reducerMethod
    );
  };

  protected patch = <ReqBody extends Object, ResBody extends Object>(
    config: HttpRequestConfig<ReqBody>,
    reducerMethod: ReducerMethods
  ) => {
    return this.requestResult<ReqBody, ResBody>(
      this.spreadConfig(config),
      HttpMethods.PATCH,
      reducerMethod
    );
  };

  protected put = <ReqBody extends Object, ResBody extends Object>(
    config: HttpRequestConfig<ReqBody>,
    reducerMethod: ReducerMethods
  ) => {
    return this.requestResult<ReqBody, ResBody>(
      this.spreadConfig(config),
      HttpMethods.PUT,
      reducerMethod
    );
  };

  protected delete = <ReqBody extends Object, ResBody extends Object>(
    config: HttpRequestConfig<ReqBody>,
    reducerMethod: ReducerMethods
  ) => {
    return this.requestResult<ReqBody, ResBody>(
      this.spreadConfig(config),
      HttpMethods.DELETE,
      reducerMethod
    );
  };
}
